import React, { useState, useEffect } from 'react'
import Box from '@material-ui/core/Box'
import Card from '@material-ui/core/Card'
import CardHeader from '@material-ui/core/CardHeader'
import CardContent from '@material-ui/core/CardContent'
import Grid from '@material-ui/core/Grid'
import Typography from '@material-ui/core/Typography'
import Divider from '@material-ui/core/Divider'
import TextField from '@material-ui/core/TextField'
import Button from '@material-ui/core/Button'
import Checkbox from '@material-ui/core/Checkbox'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import FormControl from '@material-ui/core/FormControl'
import FormLabel from '@material-ui/core/FormLabel'
import Radio from '@material-ui/core/Radio'
import RadioGroup from '@material-ui/core/RadioGroup'
import Chip from '@material-ui/core/Chip'
import Table from '@material-ui/core/Table'
import TableHead from '@material-ui/core/TableHead'
import TableBody from '@material-ui/core/TableBody'
import TableRow from '@material-ui/core/TableRow'
import TableCell from '@material-ui/core/TableCell'
import IconButton from '@material-ui/core/IconButton'
import DeleteIcon from '@material-ui/icons/Delete'
import AdminAPI from 'config/api/AdminAPI'
import { onError } from 'config/lib/errorLib'
import { NotificationManager } from 'react-notifications'

const SOLAR_BRANDS = ['solarfinder.ie', 'energyefficiency.ie']

/**
 * Do Not Contact (DNC) admin card.
 *
 * - Shows current suppression entries + a rolling period count.
 * - Add a person by phone/email (with brand checkboxes, defaulting to both
 *   solar brands).
 * - Optionally search their solar leads and cancel the pending followup
 *   email/SMS schedules when adding them.
 */
function DncCard() {
    const api = new AdminAPI()

    const [entries, setEntries] = useState([])
    const [total, setTotal] = useState(0)
    const [periodCount, setPeriodCount] = useState(0)
    const [days] = useState(30)
    const [loading, setLoading] = useState(true)

    // add form
    const [phone, setPhone] = useState('')
    const [email, setEmail] = useState('')
    const [firstName, setFirstName] = useState('')
    const [lastName, setLastName] = useState('')
    const [brandSf, setBrandSf] = useState(true)
    const [brandEe, setBrandEe] = useState(true)
    const [busy, setBusy] = useState(false)

    // lead search / schedule cancellation
    const [leads, setLeads] = useState(null)
    const [selectedLeadIds, setSelectedLeadIds] = useState([])
    const [cancelSchedules, setCancelSchedules] = useState(true)
    // 'both' = block email + phone; 'typed' = only the identifier that was entered.
    const [scope, setScope] = useState('both')

    useEffect(() => {
        loadList()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    async function loadList() {
        try {
            const res = await api.getDncList(days)
            setEntries(res.entries || [])
            setTotal(res.total || 0)
            setPeriodCount(res.periodCount || 0)
            setLoading(false)
        } catch (e) {
            onError(e)
        }
    }

    function selectedBrands() {
        const brands = []
        if (brandSf) brands.push('solarfinder.ie')
        if (brandEe) brands.push('energyefficiency.ie')
        return brands.length ? brands : SOLAR_BRANDS
    }

    // The phone/email discovered from the matched leads (first non-empty of each).
    function discoveredContacts() {
        const list = leads || []
        const dEmail = list.map((l) => (l.email || '').trim()).find(Boolean) || ''
        const dPhone = list.map((l) => (l.phone || '').trim()).find(Boolean) || ''
        return { dEmail, dPhone }
    }

    // If they entered one channel and the lead has the other, offer to block both.
    function otherChannel() {
        if (!leads || leads.length === 0) return null
        const { dEmail, dPhone } = discoveredContacts()
        const typedEmail = email.trim()
        const typedPhone = phone.trim()
        if (typedEmail && !typedPhone && dPhone) return { typed: 'email', value: dPhone, kind: 'phone' }
        if (typedPhone && !typedEmail && dEmail) return { typed: 'phone', value: dEmail, kind: 'email' }
        return null
    }

    async function doSearch() {
        if (!phone && !email) {
            NotificationManager.warning('Enter a phone or email to search')
            return
        }
        setBusy(true)
        try {
            const res = await api.searchDncLead(phone, email)
            const found = res.leads || []
            setLeads(found)
            setSelectedLeadIds([...new Set(found.map((l) => l.leadId))])
            setScope('both')
            // Prefill the name from the lead if we don't have one yet.
            const first = found[0]
            if (first) {
                if (!firstName && first.firstName) setFirstName(first.firstName)
                if (!lastName && first.lastName) setLastName(first.lastName)
            }
            if (found.length === 0) {
                NotificationManager.info('No solar leads found for this contact')
            }
        } catch (e) {
            onError(e)
        }
        setBusy(false)
    }

    function toggleLead(leadId) {
        setSelectedLeadIds((prev) =>
            prev.includes(leadId) ? prev.filter((id) => id !== leadId) : [...prev, leadId]
        )
    }

    async function doAdd() {
        const typedEmail = email.trim()
        const typedPhone = phone.trim()
        if (!typedEmail && !typedPhone) {
            NotificationManager.warning('Enter a phone or email')
            return
        }
        setBusy(true)
        try {
            // Decide which identifiers to block. 'both' pulls in the other channel
            // discovered from the lead; 'typed' blocks only what was entered.
            const { dEmail, dPhone } = discoveredContacts()
            let addEmail = typedEmail
            let addPhone = typedPhone
            if (scope === 'both') {
                if (!addEmail) addEmail = dEmail
                if (!addPhone) addPhone = dPhone
            }

            const entry = {
                phone: addPhone,
                email: addEmail,
                firstName,
                lastName,
                brands: selectedBrands(),
                leadIds: selectedLeadIds,
                cancelSchedules: cancelSchedules && selectedLeadIds.length > 0,
            }
            const res = await api.addToDnc(entry)
            const cancelledCount = res.cancelled && res.cancelled.deleted ? res.cancelled.deleted.length : 0
            NotificationManager.success(
                cancelledCount > 0
                    ? `Added to DNC — ${cancelledCount} schedule(s) cancelled`
                    : 'Added to DNC'
            )
            resetForm()
            loadList()
        } catch (e) {
            onError(e)
        }
        setBusy(false)
    }

    async function doRemove(id) {
        try {
            await api.removeFromDnc(id)
            NotificationManager.success('Removed from DNC')
            loadList()
        } catch (e) {
            onError(e)
        }
    }

    function resetForm() {
        setPhone('')
        setEmail('')
        setFirstName('')
        setLastName('')
        setBrandSf(true)
        setBrandEe(true)
        setLeads(null)
        setSelectedLeadIds([])
        setCancelSchedules(true)
        setScope('both')
    }

    return (
        <Card>
            <CardHeader
                subheader={
                    <Box component={Typography} variant='h3' marginBottom='0!important'>
                        Do Not Contact (DNC)
                    </Box>
                }
                action={
                    <Box display='flex' alignItems='center' style={{ gap: '1rem', paddingRight: '1rem' }}>
                        <Chip label={`${total} total`} color='primary' variant='outlined' />
                        <Chip label={`${periodCount} in last ${days} days`} variant='outlined' />
                    </Box>
                }
            />
            <CardContent>
                <Typography variant='body2' color='textSecondary' gutterBottom>
                    People on this list are never sent a solar email or SMS. Adding someone can also
                    cancel any pending followup emails/SMS already scheduled for their solar leads.
                </Typography>

                {/* Add form */}
                <Box marginTop='1.5rem' marginBottom='1rem'>
                    <Typography variant='h5' color='textPrimary' gutterBottom style={{ fontSize: '1.1rem' }}>
                        Add someone to the list
                    </Typography>
                    <Divider style={{ marginBottom: '1rem' }} />
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={3}>
                            <TextField label='Phone' fullWidth value={phone} onChange={(e) => setPhone(e.target.value)} />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextField label='Email' fullWidth value={email} onChange={(e) => setEmail(e.target.value)} />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextField label='First name' fullWidth value={firstName} onChange={(e) => setFirstName(e.target.value)} />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextField label='Last name' fullWidth value={lastName} onChange={(e) => setLastName(e.target.value)} />
                        </Grid>
                    </Grid>

                    <Box marginTop='1rem'>
                        <Typography variant='caption' color='textSecondary'>Brands</Typography>
                        <Box>
                            <FormControlLabel
                                control={<Checkbox checked={brandSf} onChange={(e) => setBrandSf(e.target.checked)} color='primary' />}
                                label='Solar Finder (solarfinder.ie)'
                            />
                            <FormControlLabel
                                control={<Checkbox checked={brandEe} onChange={(e) => setBrandEe(e.target.checked)} color='primary' />}
                                label='Energy Efficiency (energyefficiency.ie)'
                            />
                        </Box>
                    </Box>

                    <Box marginTop='1rem' display='flex' style={{ gap: '0.75rem' }}>
                        <Button variant='outlined' color='primary' onClick={doSearch} disabled={busy}>
                            Find solar leads &amp; schedules
                        </Button>
                        <Button variant='contained' color='primary' onClick={doAdd} disabled={busy}>
                            Add to DNC
                        </Button>
                    </Box>

                    {/* Lead search results */}
                    {leads !== null && (
                        <Box marginTop='1.5rem'>
                            {leads.length > 0 ? (
                                <>
                                    {otherChannel() && (
                                        <FormControl component='fieldset' style={{ display: 'block', marginBottom: '0.75rem' }}>
                                            <FormLabel component='legend' style={{ fontSize: '0.85rem' }}>
                                                We found this lead&apos;s {otherChannel().kind} ({otherChannel().value}). What should we block?
                                            </FormLabel>
                                            <RadioGroup value={scope} onChange={(e) => setScope(e.target.value)}>
                                                <FormControlLabel
                                                    value='both'
                                                    control={<Radio color='primary' />}
                                                    label='Email + phone (recommended — stops emails and SMS)'
                                                />
                                                <FormControlLabel
                                                    value='typed'
                                                    control={<Radio color='primary' />}
                                                    label={`${otherChannel().typed === 'email' ? 'Email' : 'Phone'} only`}
                                                />
                                            </RadioGroup>
                                        </FormControl>
                                    )}
                                    <FormControlLabel
                                        control={<Checkbox checked={cancelSchedules} onChange={(e) => setCancelSchedules(e.target.checked)} color='primary' />}
                                        label='Cancel pending followup emails/SMS for the selected leads when adding'
                                    />
                                    <Table size='small'>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell padding='checkbox' />
                                                <TableCell>Name</TableCell>
                                                <TableCell>Site</TableCell>
                                                <TableCell>Lead ID</TableCell>
                                                <TableCell>Created</TableCell>
                                                <TableCell>Schedules</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {leads.map((l) => (
                                                <TableRow key={`${l.site}-${l.leadId}`}>
                                                    <TableCell padding='checkbox'>
                                                        <Checkbox
                                                            checked={selectedLeadIds.includes(l.leadId)}
                                                            onChange={() => toggleLead(l.leadId)}
                                                            color='primary'
                                                        />
                                                    </TableCell>
                                                    <TableCell>{`${l.firstName || ''} ${l.lastName || ''}`.trim() || '—'}</TableCell>
                                                    <TableCell>{l.site}</TableCell>
                                                    <TableCell>{l.leadId}</TableCell>
                                                    <TableCell>{l.createdAt ? new Date(l.createdAt).toLocaleDateString() : '—'}</TableCell>
                                                    <TableCell>{(l.scheduleNames || []).length}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </>
                            ) : (
                                <Typography variant='body2' color='textSecondary'>
                                    No solar leads found — you can still add the contact above.
                                </Typography>
                            )}
                        </Box>
                    )}
                </Box>

                {/* Current entries */}
                <Box marginTop='2rem'>
                    <Typography variant='h5' color='textPrimary' gutterBottom style={{ fontSize: '1.1rem' }}>
                        Suppressed contacts
                    </Typography>
                    <Divider style={{ marginBottom: '1rem' }} />
                    {loading ? (
                        <Typography variant='body2' color='textSecondary'>Loading…</Typography>
                    ) : entries.length === 0 ? (
                        <Typography variant='body2' color='textSecondary'>No one on the list yet.</Typography>
                    ) : (
                        <Table size='small'>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Channel</TableCell>
                                    <TableCell>Contact</TableCell>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Brands</TableCell>
                                    <TableCell>Source</TableCell>
                                    <TableCell>Added</TableCell>
                                    <TableCell align='right' />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {entries.map((e) => (
                                    <TableRow key={e.id}>
                                        <TableCell>{e.channel}</TableCell>
                                        <TableCell>{e.channel === 'phone' ? e.phone : e.email}</TableCell>
                                        <TableCell>{`${e.firstName || ''} ${e.lastName || ''}`.trim() || '—'}</TableCell>
                                        <TableCell>{Array.isArray(e.brands) && e.brands.length ? e.brands.join(', ') : 'all'}</TableCell>
                                        <TableCell>{e.source || '—'}</TableCell>
                                        <TableCell>{e.createdAt ? new Date(e.createdAt).toLocaleDateString() : '—'}</TableCell>
                                        <TableCell align='right'>
                                            <IconButton size='small' onClick={() => doRemove(e.id)}>
                                                <DeleteIcon fontSize='small' />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </Box>
            </CardContent>
        </Card>
    )
}

export default DncCard
